// Servicio de Autenticación
import apiService from './api';
import { API_ENDPOINTS, USER_KEY, PERMISSIONS_KEY } from '@/config/api';
import type { LoginRequest, User, Permissions } from '@/types';

class AuthService {
  /**
   * Iniciar sesión
   */
  async login(credentials: LoginRequest): Promise<any> {
    // Los tokens son seteados como cookies httpOnly por el backend — no se leen aquí
    const response = await apiService.post<any>(API_ENDPOINTS.LOGIN, credentials);

    const data: any = response.data || response;

    // Si requiere 2FA no hay datos de sesión aún
    if (data.requires_2fa) {
      return response;
    }

    if (response.success && data.user && data.permissions) {
      // Solo guardamos datos no-sensibles en localStorage
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(data.permissions));
    }

    return response;
  }

  /**
   * Cerrar sesión
   */
  async logout(reason: 'manual' | 'inactivity' = 'manual'): Promise<void> {
    try {
      // El refresh_token viaja en cookie httpOnly — el backend lo lee y lo revoca
      await apiService.post(API_ENDPOINTS.LOGOUT, { reason });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      // Limpiar solo datos no-sensibles (los tokens son cookies, los limpia el backend)
      this.clearStorage();
    }
  }

  /**
   * Refrescar token (el refresh_token viaja en cookie automáticamente)
   */
  async refreshToken(): Promise<any> {
    const response = await apiService.post(API_ENDPOINTS.REFRESH, {});

    if (response.success && response.data?.permissions) {
      localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(response.data.permissions));
    }

    return response;
  }

  /**
   * Obtener usuario actual
   */
  async getMe(): Promise<User> {
    const response = await apiService.get<any>(API_ENDPOINTS.ME);

    // El endpoint /me devuelve { success, user } (no { success, data })
    const raw = response.user ?? response.data;
    if (!raw) throw new Error('No se pudo obtener el usuario');

    // Normalizar al mismo formato que retorna el login:
    // - El JWT usa 'userId' pero la app espera 'id'
    // - El JWT guarda roles como strings, el login los devuelve como objetos { id, name }
    const user: User = {
      ...raw,
      id: raw.id ?? raw.userId,
      roles: (raw.roles ?? []).map((r: any) =>
        typeof r === 'string' ? { id: 0, name: r } : r
      ),
    };

    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  }

  /**
   * Cambiar contraseña
   */
  async changePassword(currentPassword: string, newPassword: string, confirmPassword?: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
      confirmPassword: confirmPassword ?? newPassword,
    });
  }

  /**
   * Limpiar storage
   */
  clearStorage(): void {
    // Los tokens son cookies httpOnly — las limpia el backend en logout
    // Aquí solo limpiamos datos de sesión no-sensibles
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
  }

  /**
   * Obtener usuario almacenado
   */
  getStoredUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Obtener permisos almacenados
   */
  getStoredPermissions(): Permissions | null {
    const permsStr = localStorage.getItem(PERMISSIONS_KEY);
    return permsStr ? JSON.parse(permsStr) : null;
  }

  /**
   * Obtener historial de logins del usuario
   */
  async getLoginHistory(limit = 10): Promise<Array<{ timestamp: string; ip_address: string; user_agent: string }>> {
    const response = await apiService.get<any>(`/api/auth/login-history?limit=${limit}`);
    return response.data ?? [];
  }

  /**
   * Obtener sesiones activas (refresh_tokens vigentes)
   */
  async getSessions(): Promise<Array<{ id: number; created_at: string; expires_at: string; ip_address: string; user_agent: string }>> {
    const response = await apiService.get<any>('/api/auth/sessions');
    return response.data ?? [];
  }

  /**
   * Revocar una sesión activa por id
   */
  async revokeSession(sessionId: number): Promise<void> {
    await apiService.delete(`/api/auth/sessions/${sessionId}`);
  }

  // ── 2FA ──────────────────────────────────────────────────────────────────────

  async get2faStatus(): Promise<{ enabled: boolean }> {
    const response = await apiService.get<any>('/api/auth/2fa/status');
    return response.data;
  }

  async setup2fa(): Promise<{ secret: string; qr: string }> {
    const response = await apiService.post<any>('/api/auth/2fa/setup', {});
    return response.data;
  }

  async enable2fa(code: string): Promise<void> {
    await apiService.post('/api/auth/2fa/enable', { code });
  }

  async disable2fa(code: string): Promise<void> {
    await apiService.post('/api/auth/2fa/disable', { code });
  }

  async complete2faLogin(preAuthToken: string, code: string): Promise<any> {
    return apiService.post<any>('/api/auth/2fa/complete', { pre_auth_token: preAuthToken, code });
  }

  // ── Preferencias de notificaciones operacionales ──────────────────────────

  async getNotificationPreferences(): Promise<Record<string, { email: boolean }>> {
    const response = await apiService.get<any>('/api/auth/notification-preferences');
    return response.data ?? {};
  }

  async saveNotificationPreferences(prefs: Record<string, { email: boolean }>): Promise<void> {
    await apiService.put('/api/auth/notification-preferences', prefs);
  }

  /**
   * Verificar si hay datos de sesión en localStorage (indicador local, no autoritativo).
   * La verificación real la hace AuthContext llamando a /api/auth/me con la cookie.
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem(USER_KEY);
  }

  /**
   * Verificar si tiene un permiso específico
   */
  hasPermission(module: string, action: string): boolean {
    const permissions = this.getStoredPermissions();
    return permissions?.[module]?.[action] === true;
  }

  /**
   * Verificar si tiene alguno de los permisos
   */
  hasAnyPermission(checks: Array<{ module: string; action: string }>): boolean {
    const permissions = this.getStoredPermissions();
    return checks.some(({ module, action }) => permissions?.[module]?.[action] === true);
  }

  /**
   * Verificar si tiene todos los permisos
   */
  hasAllPermissions(checks: Array<{ module: string; action: string }>): boolean {
    const permissions = this.getStoredPermissions();
    return checks.every(({ module, action }) => permissions?.[module]?.[action] === true);
  }

  /**
   * Solicitar recuperación de contraseña.
   * Siempre retorna éxito (el backend previene enumeración de emails).
   */
  async forgotPassword(email: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
  }

  /**
   * Restablecer contraseña con token recibido por email.
   * Lanza error si el token es inválido o expirado.
   */
  async resetPassword(token: string, password: string): Promise<void> {
    await apiService.post(API_ENDPOINTS.RESET_PASSWORD, { token, password });
  }
}

export const authService = new AuthService();
export default authService;

/**
 * Determina la ruta de inicio correcta según los permisos del usuario.
 * Soporta tanto el formato legacy (dashboard.read) como el formato
 * de matriz (dashboard.view / dashboard.business / dashboard.operational).
 */
export function getDefaultRoute(permissions: Permissions | null): string {
  if (!permissions) return '/dashboard';

  // Comprueba un permiso de dashboard en formato matrix O legacy
  const hasDashboardPerm = (action: string): boolean => {
    if (permissions['dashboard']?.[action] === true) return true;
    // En el formato legacy, 'dashboard.read' cubre view, business y operational
    if (permissions['dashboard']?.['read'] === true) return true;
    return false;
  };

  // Prioridad: general → corporativo → operacional
  if (hasDashboardPerm('view')) return '/dashboard';
  if (hasDashboardPerm('business')) return '/corporate-dashboard';
  if (hasDashboardPerm('operational')) return '/operational-dashboard';

  // Sin acceso a ningún dashboard: primera ruta accesible
  const has = (mod: string, action: string): boolean =>
    permissions[mod]?.[action] === true;

  if (has('users', 'view') || has('user_management', 'read')) return '/users';
  if (has('roles', 'view')) return '/roles';
  if (has('controllers', 'view') || has('controllers', 'read')) return '/controllers';
  if (has('access_points', 'view') || has('access_control', 'read')) return '/access-points';
  if (has('logs', 'view') || has('audit', 'read')) return '/logs';
  // Módulos corporativos
  if (has('employees', 'view') || has('corporate', 'view')) return '/employees';
  if (has('visitors', 'view')) return '/visitor-appointments';
  if (has('departments', 'view')) return '/departments';
  if (has('sedes', 'view')) return '/sedes';
  if (has('credentials', 'view')) return '/credentials';
  if (has('access_groups', 'view')) return '/access-groups';
  if (has('spaces', 'view') || has('bookings', 'view')) return '/spaces';
  // Reportes
  if (has('reports', 'view')) return '/reports';
  // Hubs
  if (has('hubs', 'view')) return '/hubs';

  return '/profile';
}
